import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Upload, X, Check, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, useFetch } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { PageHeader, Field, NativeSelect, ErrorBox, Section, Loading, Detail } from "@/components/Common";

const STEPS = ["Property", "Work required", "Photographs", "Review"];
const PC = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;
const EMPTY = { address: "", postcode: "", property_type: "residential", access_instructions: "", parking_info: "" };

function StepBar({ step }) {
  return (
    <ol className="mb-6 grid grid-cols-4 gap-2" aria-label="Progress">
      {STEPS.map((s, i) => (
        <li key={s} aria-current={i === step ? "step" : undefined} data-testid={`wizard-step-${i}`}>
          <div className={`h-1.5 rounded-full ${i <= step ? "bg-primary" : "bg-muted"}`} />
          <p className={`mt-1.5 text-xs ${i === step ? "font-semibold text-foreground" : "text-muted-foreground"}`}>{i + 1}. {s}</p>
        </li>
      ))}
    </ol>
  );
}

function PhotoStep({ photos, setPhotos, info }) {
  const ref = useRef();
  const [progress, setProgress] = useState(null);
  const add = async (files) => {
    for (const file of Array.from(files)) {
      if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) { toast.error(`${file.name}: only JPEG, PNG or WebP images are accepted`); continue; }
      if (file.size > info.max_upload_mb * 1024 * 1024) { toast.error(`${file.name} is larger than ${info.max_upload_mb} MB`); continue; }
      const fd = new FormData();
      fd.append("file", file);
      setProgress({ name: file.name, pct: 0 });
      try {
        const r = await api.post("/customer/uploads", fd, { onUploadProgress: (e) => setProgress({ name: file.name, pct: Math.round((e.loaded / (e.total || 1)) * 100) }) });
        setPhotos((p) => [...p, { ...r.data, preview: URL.createObjectURL(file) }]);
      } catch (e) { toast.error(`${file.name}: ${errMsg(e)}`); }
    }
    setProgress(null);
  };
  const remove = async (p) => {
    try { await api.delete(`/customer/uploads/${p.id}`); setPhotos((x) => x.filter((y) => y.id !== p.id)); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Photos of the area help us understand the job. Optional - up to {info.max_photos} JPEG, PNG or WebP images, {info.max_upload_mb} MB each. Photos are stored privately and only visible to you and authorised DJ Landscapes staff.</p>
      <button type="button" onClick={() => ref.current.click()} data-testid="photo-dropzone"
        onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); add(e.dataTransfer.files); }}
        className="flex w-full flex-col items-center rounded-lg border-2 border-dashed border-input bg-muted/40 px-4 py-8 text-sm hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring">
        <Upload className="mb-2 h-6 w-6 text-primary" /> <span className="font-medium">Tap to choose photographs</span><span className="text-xs text-muted-foreground">or drag and drop them here</span>
      </button>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="photo-file-input" />
      {progress && <div data-testid="upload-progress"><p className="mb-1 text-xs">Uploading {progress.name}… {progress.pct}%</p><Progress value={progress.pct} /></div>}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {photos.map((p, i) => (
          <div key={p.id} className="relative overflow-hidden rounded-md border border-border" data-testid={`photo-preview-${i}`}>
            <img src={p.preview} alt={p.original_filename} className="aspect-square w-full object-cover" />
            <button type="button" onClick={() => remove(p)} aria-label={`Remove ${p.original_filename}`} data-testid={`photo-remove-${i}`}
              className="absolute right-1 top-1 rounded-full bg-card/90 p-1 shadow hover:bg-card"><X className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function NewRequest() {
  const { user } = useAuth();
  const { data: services } = useFetch("/services");
  const { data: info } = useFetch("/public/info");
  const saved = user.addresses?.[0];
  const [step, setStep] = useState(0);
  const [prop, setProp] = useState(saved ? { ...EMPTY, address: saved.address, postcode: saved.postcode } : EMPTY);
  const [work, setWork] = useState({ service_ids: [], description: "", area: "", budget: "", timeframe: "", deadline: "", requirements: "" });
  const [photos, setPhotos] = useState([]);
  const [errs, setErrs] = useState({});
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  if (!services || !info) return <Loading />;

  const validate = () => {
    const x = {};
    if (step === 0) {
      if (prop.address.trim().length < 5) x.address = "Enter the property address";
      if (!PC.test(prop.postcode.trim())) x.postcode = "Enter a valid UK postcode, e.g. SW1A 1AA";
    }
    if (step === 1) {
      if (!work.service_ids.length) x.services = "Select at least one type of work";
      if (work.description.trim().length < 10) x.description = "Please describe the work (at least 10 characters)";
    }
    setErrs(x);
    return !Object.keys(x).length;
  };
  const next = () => validate() && setStep(step + 1);
  const toggle = (id) => setWork((w) => ({ ...w, service_ids: w.service_ids.includes(id) ? w.service_ids.filter((s) => s !== id) : [...w.service_ids, id] }));
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post("/customer/requests", { property: prop, ...work, photo_ids: photos.map((p) => p.id) });
      setDone(r.data);
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  const P = (k) => ({ value: prop[k], onChange: (e) => setProp({ ...prop, [k]: e.target.value }) });
  const W = (k) => ({ value: work[k], onChange: (e) => setWork({ ...work, [k]: e.target.value }) });

  if (done) return (
    <div className="mx-auto max-w-xl rounded-lg border border-border bg-card p-8 text-center" data-testid="request-confirmation">
      <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-primary" />
      <h1 className="font-heading text-2xl font-semibold">Request submitted</h1>
      <p className="mt-2 text-sm text-muted-foreground">Your reference number is</p>
      <p className="mt-1 font-mono text-2xl font-semibold" data-testid="request-reference">{done.reference}</p>
      <p className="mt-3 text-sm">Saved on {fmtDateTime(done.created_at)}. Status: <strong>Request Submitted</strong>. DJ Landscapes will review it and update you here in your portal.</p>
      <div className="mt-6 flex flex-wrap justify-center gap-2">
        <Button asChild data-testid="confirm-book-appt-btn"><Link to={`/portal/appointments/book?request=${done.id}`}>Request a consultation</Link></Button>
        <Button asChild variant="outline" data-testid="confirm-view-request-btn"><Link to={`/portal/requests/${done.id}`}>View request</Link></Button>
      </div>
    </div>
  );

  const chosen = services.filter((s) => work.service_ids.includes(s.id));
  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="New job request" subtitle="Four short steps. You can go back and change anything before submitting." />
      <StepBar step={step} />
      <Section>
        {step === 0 && (
          <div className="grid gap-4" data-testid="step-property">
            <Field label="Property address" id="addr" error={errs.address} required><Textarea id="addr" rows={2} {...P("address")} data-testid="prop-address-input" /></Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Postcode" id="pc" error={errs.postcode} required><Input id="pc" autoComplete="postal-code" {...P("postcode")} data-testid="prop-postcode-input" /></Field>
              <Field label="Property type" id="ptype"><NativeSelect id="ptype" {...P("property_type")} data-testid="prop-type-select"><option value="residential">Residential</option><option value="commercial">Commercial</option></NativeSelect></Field>
            </div>
            <Field label="Access instructions (optional)" id="access" hint="e.g. side gate code, dogs on site"><Textarea id="access" rows={2} {...P("access_instructions")} data-testid="prop-access-input" /></Field>
            <Field label="Parking or site access (optional)" id="parking"><Textarea id="parking" rows={2} {...P("parking_info")} data-testid="prop-parking-input" /></Field>
          </div>
        )}
        {step === 1 && (
          <div className="grid gap-4" data-testid="step-work">
            <fieldset>
              <legend className="mb-2 text-sm font-medium">Type of work <span className="text-rose-700">*</span></legend>
              <div className="grid gap-2 sm:grid-cols-2">
                {services.map((s) => (
                  <label key={s.id} className={`flex cursor-pointer items-center gap-3 rounded-md border p-3 text-sm ${work.service_ids.includes(s.id) ? "border-primary bg-secondary" : "border-border"}`}>
                    <Checkbox checked={work.service_ids.includes(s.id)} onCheckedChange={() => toggle(s.id)} data-testid={`service-option-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} /> {s.name}
                  </label>
                ))}
              </div>
              {errs.services && <p className="mt-1 text-xs text-rose-700" role="alert">{errs.services}</p>}
            </fieldset>
            <Field label="Describe your project" id="desc" error={errs.description} required><Textarea id="desc" rows={5} {...W("description")} data-testid="work-description-input" /></Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Approximate area (if known)" id="area" hint="Rough estimates are fine"><Input id="area" placeholder="e.g. 20 m² or 5m x 4m" {...W("area")} data-testid="work-area-input" /></Field>
              <Field label="Approximate budget (optional)" id="budget"><NativeSelect id="budget" {...W("budget")} data-testid="work-budget-select">
                <option value="">Prefer not to say</option><option>Under £2,000</option><option>£2,000 - £5,000</option><option>£5,000 - £10,000</option><option>£10,000 - £25,000</option><option>Over £25,000</option>
              </NativeSelect></Field>
              <Field label="Preferred start" id="tf"><NativeSelect id="tf" {...W("timeframe")} data-testid="work-timeframe-select">
                <option value="">Flexible</option><option>As soon as possible</option><option>Within 1 month</option><option>Within 1-3 months</option><option>Within 3-6 months</option><option>Later than 6 months</option>
              </NativeSelect></Field>
              <Field label="Specific deadline (optional)" id="dl"><Input id="dl" placeholder="e.g. before a family event in June" {...W("deadline")} data-testid="work-deadline-input" /></Field>
            </div>
            <Field label="Other requirements (optional)" id="reqs"><Textarea id="reqs" rows={2} {...W("requirements")} data-testid="work-requirements-input" /></Field>
          </div>
        )}
        {step === 2 && <PhotoStep photos={photos} setPhotos={setPhotos} info={info} />}
        {step === 3 && (
          <div className="space-y-5" data-testid="step-review">
            <dl className="grid gap-4 sm:grid-cols-2">
              <Detail label="Address">{prop.address}</Detail><Detail label="Postcode">{prop.postcode.toUpperCase()}</Detail>
              <Detail label="Property type"><span className="capitalize">{prop.property_type}</span></Detail><Detail label="Access">{prop.access_instructions}</Detail>
              <Detail label="Services">{chosen.map((s) => s.name).join(", ")}</Detail><Detail label="Area">{work.area}</Detail>
              <Detail label="Budget">{work.budget || "Not given"}</Detail><Detail label="Preferred start">{work.timeframe || "Flexible"}</Detail>
              <Detail label="Deadline">{work.deadline}</Detail><Detail label="Photographs">{photos.length} attached</Detail>
            </dl>
            <Detail label="Description"><span className="whitespace-pre-wrap">{work.description}</span></Detail>
            <ErrorBox error={err} />
          </div>
        )}
        <div className="mt-6 flex justify-between gap-2 border-t border-border pt-4">
          <Button variant="outline" onClick={() => setStep(step - 1)} disabled={step === 0 || busy} data-testid="wizard-back-btn">Back</Button>
          {step < 3 ? <Button onClick={next} data-testid="wizard-next-btn">Continue</Button>
            : <Button onClick={submit} disabled={busy} data-testid="wizard-submit-btn"><Check className="mr-1 h-4 w-4" />{busy ? "Submitting…" : "Submit request"}</Button>}
        </div>
      </Section>
    </div>
  );
}
